import React, { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// @ts-ignore — @emoji-mart/react has no bundled types
import Picker from '@emoji-mart/react';
// @ts-ignore
import emojiData from '@emoji-mart/data';
import {
  Plus, Edit2, Trash2, Check, X,
  GripVertical, Loader2, ChevronDown, ChevronRight, FileText,
  ArrowLeftRight, Copy, Send,
} from 'lucide-react';
import { helpCenterAPI, knowledgeAPI, agentsAPI } from '../../services/api';
import { HelpCenterCollection, KnowledgeSource, Agent } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../ConfirmModal';

// ---- Emoji picker (emoji-mart) -----------------------------------------
const EmojiPickerPopover: React.FC<{
  onSelect: (emoji: string) => void;
  onClose: () => void;
}> = ({ onSelect, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={ref} className="absolute z-30 mt-1" style={{ top: '100%', left: 0 }}>
      <Picker
        data={emojiData}
        onEmojiSelect={(emoji: any) => { onSelect(emoji.native); onClose(); }}
        theme="auto"
        previewPosition="none"
        skinTonePosition="none"
      />
    </div>
  );
};

// ---- Sortable drag-handle wrapper for collections ----------------------
const SortableCollectionWrapper: React.FC<{
  id: string;
  children: (dragHandleProps: React.HTMLAttributes<HTMLButtonElement>) => React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
};

// ---- Main component ----------------------------------------------------
const CollectionManager: React.FC<{ agentId?: string }> = ({ agentId }) => {
  const { showToast } = useToast();
  const [collections, setCollections] = useState<HelpCenterCollection[]>([]);
  const [loading, setLoading] = useState(true);

  // New collection form
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColEmoji, setNewColEmoji] = useState('📚');
  const [showNewEmojiPicker, setShowNewEmojiPicker] = useState(false);
  const [savingNewCol, setSavingNewCol] = useState(false);

  // Edit collection inline
  const [editingColId, setEditingColId] = useState<string | null>(null);
  const [editColName, setEditColName] = useState('');
  const [editColEmoji, setEditColEmoji] = useState('');
  const [showEditEmojiPicker, setShowEditEmojiPicker] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Article management
  const [articles, setArticles] = useState<KnowledgeSource[]>([]);
  const [expandedColId, setExpandedColId] = useState<string | null>(null);
  const [addPickerColId, setAddPickerColId] = useState<string | null>(null);

  // Transfer (bulk copy/move to another agent)
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [transferAction, setTransferAction] = useState<'move' | 'copy' | null>(null);
  const [targetAgentId, setTargetAgentId] = useState<string>('');
  const [transferring, setTransferring] = useState(false);
  const [includeArticles, setIncludeArticles] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { fetchCollections(); }, [agentId]);
  useEffect(() => { if (agentId) fetchArticles(); }, [agentId]);
  useEffect(() => {
    agentsAPI.list()
      .then(res => setAvailableAgents(res.data ?? []))
      .catch(() => setAvailableAgents([]));
  }, []);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const res = await helpCenterAPI.getCollections(agentId);
      setCollections(res.data);
    } catch {
      showToast('Failed to load collections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    if (!agentId) return;
    try {
      const res = await knowledgeAPI.list(agentId);
      setArticles(res.data);
    } catch {
      // non-fatal
    }
  };

  const articlesForCollection = (colId: string) =>
    articles.filter(a => a.collection_id === colId);

  const unassignedArticles = articles.filter(a => !a.collection_id);

  const handleRemoveFromCollection = async (articleId: string) => {
    try {
      await knowledgeAPI.bulkCollection([articleId], null);
      setArticles(prev => prev.map(a => a.id === articleId ? { ...a, collection_id: undefined } : a));
      setCollections(prev => prev.map(c => c.article_count ? { ...c, article_count: articlesForCollection(c.id).filter(a => a.id !== articleId).length } : c));
      showToast('Article removed from collection', 'success');
    } catch {
      showToast('Failed to remove article', 'error');
    }
  };

  const handleAddToCollection = async (articleId: string, colId: string) => {
    try {
      await knowledgeAPI.bulkCollection([articleId], colId);
      setArticles(prev => prev.map(a => a.id === articleId ? { ...a, collection_id: colId } : a));
      showToast('Article added to collection', 'success');
    } catch {
      showToast('Failed to add article', 'error');
    }
  };

  // ---- Drag-and-drop handlers ------------------------------------------

  const handleCollectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = collections.findIndex(c => c.id === active.id);
    const newIndex = collections.findIndex(c => c.id === over.id);
    const reordered = arrayMove(collections, oldIndex, newIndex).map((c, i) => ({ ...c, order: i }));
    setCollections(reordered);
    helpCenterAPI
      .reorderCollections(reordered.map(c => ({ id: c.id, order: c.order })))
      .catch(() => { showToast('Failed to reorder', 'error'); fetchCollections(); });
  };

  // ---- Collection CRUD -------------------------------------------------

  const handleCreateCollection = async () => {
    if (!newColName.trim()) return;
    setSavingNewCol(true);
    try {
      const res = await helpCenterAPI.createCollection({
        name: newColName.trim(),
        emoji: newColEmoji,
        order: collections.length,
      });
      setCollections(prev => [...prev, res.data]);
      setNewColName('');
      setNewColEmoji('📚');
      setShowNewCollection(false);
      showToast('Collection created', 'success');
    } catch {
      showToast('Failed to create collection', 'error');
    } finally {
      setSavingNewCol(false);
    }
  };

  const startEditCollection = (col: HelpCenterCollection) => {
    setEditingColId(col.id);
    setEditColName(col.name);
    setEditColEmoji(col.emoji ?? '📚');
  };

  const handleSaveCollection = async (id: string) => {
    try {
      const res = await helpCenterAPI.updateCollection(id, {
        name: editColName.trim(),
        emoji: editColEmoji,
      });
      setCollections(prev => prev.map(c => c.id === id ? res.data : c));
      setEditingColId(null);
      showToast('Collection updated', 'success');
    } catch {
      showToast('Failed to update collection', 'error');
    }
  };

  const handleDeleteCollection = async (id: string) => {
    try {
      await helpCenterAPI.deleteCollection(id);
      setCollections(prev => prev.filter(c => c.id !== id));
      // Freed articles become uncategorized
      setArticles(prev => prev.map(a => a.collection_id === id ? { ...a, collection_id: undefined } : a));
      showToast('Collection deleted', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Failed to delete collection';
      showToast(msg, 'error');
    }
  };

  // ---- Transfer (bulk copy/move) ---------------------------------------

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const openTransferModal = (action: 'move' | 'copy') => {
    const others = availableAgents.filter(a => a.agent_id !== agentId);
    setTargetAgentId(others[0]?.agent_id ?? '');
    setTransferAction(action);
  };

  const closeTransferModal = () => {
    setTransferAction(null);
    setTargetAgentId('');
  };

  const selectedArticleCount = articles.filter(
    a => a.collection_id && selectedIds.includes(a.collection_id)
  ).length;

  const handleTransfer = async () => {
    if (!agentId || !targetAgentId || !transferAction || selectedIds.length === 0) return;
    setTransferring(true);
    try {
      const res = await knowledgeAPI.bulkCollectionTransfer({
        collection_ids: selectedIds,
        source_agent_id: agentId,
        target_agent_id: targetAgentId,
        action: transferAction,
        include_articles: includeArticles,
      });
      const verb = res.data.action === 'move' ? 'Moved' : 'Copied';
      const msg = includeArticles
        ? `${verb} ${res.data.transferred} article(s) across ${res.data.collections} collection(s)`
        : `${verb} ${res.data.collections} collection(s) (structure only)`;
      showToast(msg, 'success');
      closeTransferModal();
      exitSelectionMode();
      fetchCollections();
      fetchArticles();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Transfer failed';
      showToast(msg, 'error');
    } finally {
      setTransferring(false);
    }
  };

  // ---- Render ----------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">
          Collections
          {selectionMode && selectedIds.length > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {selectedIds.length} selected
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {selectionMode ? (
            <button
              onClick={exitSelectionMode}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          ) : (
            <>
              {availableAgents.length > 1 && collections.length > 0 && (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  Transfer
                </button>
              )}
              <button
                onClick={() => setShowNewCollection(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New Collection
              </button>
            </>
          )}
        </div>
      </div>

      {collections.length === 0 && !showNewCollection && (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          No collections yet. Create one to organize your Help Center articles.
        </p>
      )}

      {/* Sortable collection list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleCollectionDragEnd}
      >
        <SortableContext
          items={collections.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {collections.map(col => (
              <SortableCollectionWrapper key={col.id} id={col.id}>
                {dragHandleProps => (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-visible">
                    {/* Collection header */}
                    <div
                      className={`flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 group rounded-xl ${
                        selectionMode ? 'cursor-pointer' : ''
                      } ${selectionMode && selectedIds.includes(col.id) ? 'ring-2 ring-indigo-500 dark:ring-indigo-400' : ''}`}
                      onClick={selectionMode ? () => toggleSelection(col.id) : undefined}
                    >
                      {selectionMode && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(col.id)}
                          onChange={() => toggleSelection(col.id)}
                          onClick={e => e.stopPropagation()}
                          className="flex-shrink-0 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                      )}
                      {/* Drag handle */}
                      <button
                        {...dragHandleProps}
                        disabled={selectionMode}
                        className="flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-gray-500 cursor-grab active:cursor-grabbing disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Drag to reorder collection"
                        tabIndex={-1}
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>

                      {editingColId === col.id ? (
                        /* Inline edit */
                        <div className="flex flex-1 items-center gap-2 flex-wrap">
                          <div className="relative">
                            <button
                              onClick={() => setShowEditEmojiPicker(p => !p)}
                              className="text-xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                            >
                              {editColEmoji}
                            </button>
                            {showEditEmojiPicker && (
                              <EmojiPickerPopover
                                onSelect={setEditColEmoji}
                                onClose={() => setShowEditEmojiPicker(false)}
                              />
                            )}
                          </div>
                          <input
                            type="text"
                            value={editColName}
                            onChange={e => setEditColName(e.target.value)}
                            autoFocus
                            className="flex-1 min-w-0 text-sm px-2 py-1 border border-indigo-300 dark:border-indigo-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                          <button
                            onClick={() => handleSaveCollection(col.id)}
                            disabled={!editColName.trim()}
                            className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingColId(null)}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        /* Display */
                        <>
                          {/* Expand/collapse toggle */}
                          <button
                            onClick={e => { e.stopPropagation(); setExpandedColId(prev => prev === col.id ? null : col.id); }}
                            disabled={selectionMode}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {expandedColId === col.id
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <span className="text-xl">{col.emoji ?? '📚'}</span>
                          <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white min-w-0 truncate">
                            {col.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full flex-shrink-0">
                            {articlesForCollection(col.id).length} articles
                          </span>
                          {!selectionMode && (
                            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 flex-shrink-0 transition-opacity">
                              <button
                                onClick={e => { e.stopPropagation(); startEditCollection(col); }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={e => { e.stopPropagation(); setDeleteTarget({ id: col.id, name: col.name }); }}
                                className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Expanded article list */}
                    {expandedColId === col.id && (
                      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-2">
                        {articlesForCollection(col.id).length === 0 ? (
                          <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">No articles in this collection</p>
                        ) : (
                          articlesForCollection(col.id).map(article => (
                            <div key={article.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 group/article">
                              <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">{article.title}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                article.status === 'published' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {article.status ?? 'published'}
                              </span>
                              <button
                                onClick={() => handleRemoveFromCollection(article.id)}
                                className="opacity-0 group-hover/article:opacity-100 p-1 text-gray-400 hover:text-red-500 rounded transition-opacity"
                                title="Remove from collection"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))
                        )}

                        {/* Add articles picker */}
                        <div className="relative pt-1">
                          {addPickerColId === col.id ? (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 max-h-48 overflow-y-auto">
                              {unassignedArticles.length === 0 ? (
                                <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No unassigned articles</p>
                              ) : (
                                unassignedArticles.map(article => (
                                  <button
                                    key={article.id}
                                    onClick={() => handleAddToCollection(article.id, col.id)}
                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                  >
                                    <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{article.title}</span>
                                  </button>
                                ))
                              )}
                              <button
                                onClick={() => setAddPickerColId(null)}
                                className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 border-t border-gray-100 dark:border-gray-700"
                              >
                                Close
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setAddPickerColId(col.id)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                              Add Articles
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SortableCollectionWrapper>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* New collection form */}
      {showNewCollection && (
        <div className="border-2 border-dashed border-indigo-300 dark:border-indigo-700 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">New Collection</p>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <button
                onClick={() => setShowNewEmojiPicker(p => !p)}
                className="text-2xl w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {newColEmoji}
              </button>
              {showNewEmojiPicker && (
                <EmojiPickerPopover
                  onSelect={setNewColEmoji}
                  onClose={() => setShowNewEmojiPicker(false)}
                />
              )}
            </div>
            <input
              type="text"
              value={newColName}
              onChange={e => setNewColName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateCollection(); }}
              autoFocus
              placeholder="Collection name"
              className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateCollection}
              disabled={!newColName.trim() || savingNewCol}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {savingNewCol ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save
            </button>
            <button
              onClick={() => { setShowNewCollection(false); setNewColName(''); }}
              className="px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) handleDeleteCollection(deleteTarget.id);
        }}
        title="Delete Collection"
        message={(() => {
          const count = deleteTarget ? articlesForCollection(deleteTarget.id).length : 0;
          return count > 0
            ? `Deleting this collection will remove ${count} article(s) from it. They will become uncategorized but won't be deleted.`
            : `Articles in this collection will become uncategorized but won't be deleted.`;
        })()}
        confirmText="Delete"
        isDangerous
      />

      {/* Floating action bar for selection mode */}
      {selectionMode && selectedIds.length > 0 && (
        <div className="sticky bottom-4 z-20 mt-4">
          <div className="mx-auto max-w-xl rounded-2xl shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 min-w-0">
              {includeArticles
                ? `${selectedIds.length} collection${selectedIds.length === 1 ? '' : 's'} · ${selectedArticleCount} article${selectedArticleCount === 1 ? '' : 's'}`
                : `${selectedIds.length} collection${selectedIds.length === 1 ? '' : 's'} (structure only)`}
            </span>
            <button
              onClick={() => openTransferModal('copy')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy to…
            </button>
            <button
              onClick={() => openTransferModal('move')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Move to…
            </button>
          </div>
        </div>
      )}

      {/* Transfer modal */}
      {transferAction && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
          onClick={closeTransferModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-5 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {transferAction === 'copy' ? 'Copy collections' : 'Move collections'}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {transferAction === 'copy' ? 'Copy' : 'Move'} {selectedIds.length} collection{selectedIds.length === 1 ? '' : 's'}
                {includeArticles ? ` (${selectedArticleCount} article${selectedArticleCount === 1 ? '' : 's'})` : ''} to another agent.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target Agent
              </label>
              {availableAgents.filter(a => a.agent_id !== agentId).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                  No other agents available.
                </p>
              ) : (
                <select
                  value={targetAgentId}
                  onChange={e => setTargetAgentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {availableAgents
                    .filter(a => a.agent_id !== agentId)
                    .map(a => (
                      <option key={a.agent_id} value={a.agent_id}>
                        {a.config.agent_name}
                      </option>
                    ))}
                </select>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Include articles</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {includeArticles
                      ? `Articles inside the selected collections will be ${transferAction === 'copy' ? 'copied' : 'moved'}.`
                      : 'Only collection structure will be transferred. Empty collections will appear under the target agent.'}
                  </p>
                </div>
                <button
                  onClick={() => setIncludeArticles(v => !v)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    includeArticles ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  aria-label="Toggle include articles"
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      includeArticles ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={closeTransferModal}
                disabled={transferring}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring || !targetAgentId}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {transferring
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : transferAction === 'copy'
                    ? <Copy className="w-3.5 h-3.5" />
                    : <Send className="w-3.5 h-3.5" />}
                {transferAction === 'copy' ? 'Copy' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionManager;
